import { Button, Divider, Tooltip, Skeleton } from 'antd'
import isNil from 'lodash-es/isNil'
import { FunctionComponent, ReactNode, useMemo, useState } from 'react'
import styled, { useTheme } from 'styled-components'
import Blur from '@/components/common/blur'
import { Duration } from '@/components/common/duration-select'
import getLevel, { Level } from '@/components/common/k-score/get-level'
import { Icon } from '@/components/custom-icon'
import useKolPlatform from '@/components/kol/hooks/use-kol-platform'
import RadarChartModal from '@/components/modal/radar-chart-modal'
import { KolDetailDuration } from '@/hooks/use-authorization/routes'
import { usePermissions } from '@/hooks/use-permissions'
import { useIntl } from '@/i18n/hooks/use-intl'
import { Nullable } from '@/types/nullable'
import { KolSchemaModel } from '@/types/schema/kol-schema'
import { ampli } from '@/utils/ampli'
import { PlatformShortcode } from '@/utils/convert-platform'
import { getPrDistributions } from '@/utils/get-k-score-pr'
import { isWithinPastOneMonth } from '@/utils/time/is-within-past-days'

interface KScoreProps {
  loading?: boolean
  platformShortcode?: PlatformShortcode
  isKolInfoLocked: boolean
  showMockData?: boolean
  duration?: KolDetailDuration
  kolInfo?: KolSchemaModel
}

const KScore: FunctionComponent<KScoreProps> = ({
  loading,
  isKolInfoLocked,
  showMockData,
  kolInfo,
  duration = Duration.ThreeMonths,
  platformShortcode,
}) => {
  const { formatMessage } = useIntl()
  const theme = useTheme()
  const { withKolPermission } = usePermissions()
  const { kolPlatformInfo } = useKolPlatform({
    platform: platformShortcode,
    kolId: kolInfo?.id,
  })
  const [radarChartModalVisible, setRadarChartModalVisible] = useState(false)

  const isBlur = isKolInfoLocked && !showMockData
  const kScorePlatforms = [
    PlatformShortcode.Facebook,
    PlatformShortcode.Instagram,
    PlatformShortcode.YouTube,
  ]

  const display =
    kolInfo &&
    platformShortcode &&
    kScorePlatforms.includes(platformShortcode) &&
    !withKolPermission

  const [
    { scoreColor, levelI18nId, type, descriptionI18nId },
    score,
    prValues,
  ] = useMemo((): [Level, Nullable<number> | undefined, number[]] => {
    if (!!showMockData || isKolInfoLocked) {
      return [getLevel(95), 95, []]
    }

    const kpi = kolInfo?.kpi?.find((kpi) => kpi.platform === platformShortcode)
    const score =
      duration === Duration.ThreeMonths ? kpi?.score3m : kpi?.score6m
    const prValues =
      kpi && platformShortcode ? getPrDistributions(kpi, platformShortcode) : []
    return [getLevel(score), score, prValues]
  }, [kolInfo?.kpi, platformShortcode, isKolInfoLocked, showMockData, duration])

  const isNewKol = useMemo((): boolean => {
    if (showMockData ?? isKolInfoLocked) {
      return false
    }

    const createdDate = kolPlatformInfo?.dc_created_time ?? ''

    return isWithinPastOneMonth(createdDate) && isNil(score)
  }, [isKolInfoLocked, showMockData, kolPlatformInfo?.dc_created_time, score])

  const handleRadarChartModalVisible = (visible: boolean): void => {
    if (!kolInfo) {
      return
    }

    setRadarChartModalVisible(visible)

    if (visible) {
      ampli.clickKScoreDetail({
        kolId: kolInfo.id,
        kolName: kolInfo.name,
      })
    } else {
      ampli.closeKScoreDetail({
        kolId: kolInfo.id,
        kolName: kolInfo.name,
      })
    }
  }

  if (!display) {
    return null
  }

  if (loading) {
    return (
      <StyledSkeleton
        active
        paragraph={{ rows: 1, width: '90%' }}
        title={{ width: '100%' }}
      />
    )
  }

  const hasKScore = kolInfo && !isNil(score) && score !== 0

  return (
    <Wrapper>
      {((): ReactNode => {
        const [color, value] = hasKScore
          ? [scoreColor, score]
          : [theme.colors.text.placeholder, '--']
        return (
          <ScoreWrapper $color={color}>
            <Blur activate={isBlur}>{value}</Blur>
          </ScoreWrapper>
        )
      })()}
      <Divider type='vertical' />
      <InfoWrapper>
        <Title>
          {formatMessage({ id: 'kol:influencer_scoring' })}:{' '}
          <Blur activate={isBlur}>
            {isNewKol || !hasKScore
              ? formatMessage({ id: 'autoreport:chart_label_empty_data' })
              : formatMessage({ id: levelI18nId })}
          </Blur>
          <Tooltip
            placement='right'
            title={formatMessage({ id: 'kol:score_tooltip' })}
          >
            <HelpCircle type='help-circle' />
          </Tooltip>
        </Title>
        <Blur activate={isBlur}>
          <Description>
            {isNewKol
              ? formatMessage({
                  id: 'kol:influencer_scoring_is_new_monthly_calculation',
                })
              : formatMessage({ id: descriptionI18nId })}
            {hasKScore && (
              <Button
                style={{ padding: 0 }}
                type='link'
                onClick={(): void => {
                  handleRadarChartModalVisible(true)
                }}
              >
                {formatMessage({ id: 'general:view_instructions' })}
              </Button>
            )}
          </Description>
        </Blur>
      </InfoWrapper>

      <RadarChartModal
        data={prValues}
        descriptionI18nId={descriptionI18nId}
        isKolInfoLocked={isKolInfoLocked}
        levelI18nId={levelI18nId}
        open={radarChartModalVisible}
        score={score ?? 0}
        type={type}
        onClose={(): void => {
          handleRadarChartModalVisible(false)
        }}
      />
    </Wrapper>
  )
}

export default KScore

const Wrapper = styled.div`
  display: flex;
  margin-bottom: 40px;
`

const ScoreWrapper = styled.div<{ $color: string }>`
  display: flex;
  padding: 5px 20px 5px 0;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-right: 1px solid ${({ theme }): string => theme.colors.border.border};
  font-size: 36px;
  font-weight: 700;
  white-space: nowrap;

  color: ${({ $color }): string => $color};
`

const InfoWrapper = styled.div`
  display: flex;
  padding-left: 20px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 5px;
`

const Title = styled.div`
  display: flex;
  color: ${({ theme }): string => theme.colors.base.grey.grey1};
  font-size: 16px;
  font-weight: 700;
  line-height: 140%;
`

const Description = styled.div`
  color: ${({ theme }): string => theme.colors.base.grey.grey2};
  font-size: 13px;
  line-height: 140%;

  > button {
    text-decoration: underline;
  }
`

const HelpCircle = styled(Icon)`
  font-size: 16px;
  margin-left: 4px;

  svg {
    fill: none;
    stroke: ${({ theme }): string => theme.colors.base.grey.grey1};
  }
`

const StyledSkeleton = styled(Skeleton)`
  .ant-skeleton-content {
    width: 100%;

    .ant-skeleton-title {
      margin-bottom: 10px !important;
    }

    .ant-skeleton-paragraph {
      margin-top: 0 !important;
      margin-bottom: 40px !important;
    }
  }
`
